import { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useLocalization } from '@/contexts/LocalizationContext'

export interface PaginationProps {
  /** Current page, 1-based. */
  page: number
  /** Rows per page. */
  pageSize: number
  /** Total number of items across all pages. */
  totalItems: number
  onPageChange: (page: number) => void
  /** Omit to hide the rows-per-page selector. */
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  className?: string
}

/** Total pages for a given item count (never less than 1). */
export function getPageCount(totalItems: number, pageSize: number): number {
  return Math.max(1, Math.ceil(totalItems / Math.max(1, pageSize)))
}

/**
 * Build the page buttons: first and last page always shown, a window around
 * the current page, and `null` where a gap is collapsed into an ellipsis.
 */
function buildPageItems(page: number, pageCount: number): (number | null)[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  const items: (number | null)[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pageCount - 1, page + 1)

  if (start > 2) items.push(null)
  for (let i = start; i <= end; i++) items.push(i)
  if (end < pageCount - 1) items.push(null)
  items.push(pageCount)

  return items
}

/**
 * Pagination bar: item range on one side, page controls on the other.
 * Direction-aware — the chevrons follow the document's RTL/LTR direction.
 */
export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}: PaginationProps) {
  const { t, isRTL } = useLocalization()

  const pageCount = getPageCount(totalItems, pageSize)
  const currentPage = Math.min(Math.max(1, page), pageCount)
  const firstItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const lastItem = Math.min(currentPage * pageSize, totalItems)

  const pageItems = useMemo(() => buildPageItems(currentPage, pageCount), [currentPage, pageCount])

  const PrevIcon = isRTL ? ChevronRight : ChevronLeft
  const NextIcon = isRTL ? ChevronLeft : ChevronRight

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-3 pt-4',
        className
      )}
    >
      <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
        <span>
          {t('common.showingRange', {
            from: firstItem,
            to: lastItem,
            total: totalItems,
          })}
        </span>

        {onPageSizeChange && (
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-8 w-[110px] text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {t('common.perPage', { count: size })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2 sm:px-3"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label={t('common.previous')}
        >
          <PrevIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{t('common.previous')}</span>
        </Button>

        {pageItems.map((item, i) =>
          item === null ? (
            <span key={`gap-${i}`} className="px-1 text-muted-foreground select-none">
              …
            </span>
          ) : (
            <Button
              key={item}
              variant={item === currentPage ? 'default' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0 text-xs sm:text-sm"
              onClick={() => onPageChange(item)}
              aria-current={item === currentPage ? 'page' : undefined}
            >
              {item}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2 sm:px-3"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= pageCount}
          aria-label={t('common.next')}
        >
          <span className="hidden sm:inline">{t('common.next')}</span>
          <NextIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
